import type {
  CustomerNotification,
  CustomerNotificationEntityType,
  CustomerNotificationEventType,
} from "@mbbe/types";
export type NotificationKind =
  "support" | "task" | "approval" | "report" | "payment" | "project" | "file";
export type NotificationLifecycle =
  "action-required" | "team-reply" | "completed" | "rejected-error";
export interface NotificationPresentation {
  kind: NotificationKind;
  lifecycle: NotificationLifecycle;
  actionRequired: boolean;
}
const p = (
  kind: NotificationKind,
  lifecycle: NotificationLifecycle = "team-reply",
  actionRequired = false,
): NotificationPresentation => ({ kind, lifecycle, actionRequired });
const PRESENTATION: Record<
  CustomerNotificationEventType,
  NotificationPresentation
> = {
  support_issue_comment: p("support"),
  support_issue_status_changed: p("support"),
  support_issue_waiting_customer: p("support", "action-required", true),
  support_issue_resolved: p("support", "completed"),
  support_issue_reopen_update: p("support"),
  client_task_assigned: p("task", "action-required", true),
  client_task_approval_required: p("approval", "action-required", true),
  client_task_comment: p("task"),
  client_task_revision_requested: p("task", "action-required", true),
  client_task_completed: p("task", "completed"),
  client_task_due_soon: p("task", "action-required", true),
  client_task_overdue: p("task", "action-required", true),
  advanced_approval_required: p("approval", "action-required", true),
  progress_report_published: p("report"),
  payment_milestone_created: p("payment"),
  payment_due_soon: p("payment", "action-required", true),
  payment_overdue: p("payment", "action-required", true),
  payment_confirmed: p("payment", "completed"),
  project_delivery_due_soon: p("project", "action-required", true),
  project_delivery_overdue: p("project", "action-required", true),
  project_delivery_published: p("project"),
  project_delivery_delivered: p("project", "completed"),
  project_delivery_cancelled: p("project", "rejected-error"),
  project_schedule_changed: p("project"),
  file_shared: p("file"),
  scope_change_approval_due_soon: p("payment", "action-required", true),
  meeting_reservation_created: p("project"),
  meeting_reservation_rescheduled: p("project"),
  meeting_reservation_cancelled: p("project", "rejected-error"),
  meeting_slot_cancelled: p("project", "rejected-error"),
  project_closed: p("project", "completed"),
  project_reopened: p("project"),
};
export const notificationPresentation = (type: CustomerNotificationEventType) =>
  PRESENTATION[type];
export function localizedNotificationCopy(
  n: CustomerNotification,
  language: "tr" | "en",
) {
  const copy = n.data.localized;
  if (!copy) return { title: n.title, body: n.body };
  return language === "tr"
    ? { title: copy.titleTr || n.title, body: copy.bodyTr || n.body }
    : { title: copy.titleEn || n.title, body: copy.bodyEn || n.body };
}
const assertNever = (value: never): never => {
  throw new Error(`Unsupported notification entity: ${String(value)}`);
};
const safePath = (path: string) =>
  path.startsWith("/") && !path.startsWith("//") ? path : null;
export function notificationPortalHref(
  n: CustomerNotification,
  supportBase = "/support",
): string {
  const entityType: CustomerNotificationEntityType = n.entityType;
  switch (entityType) {
    case "support_issue":
      return `${supportBase}/issue/${encodeURIComponent(n.entityId)}`;
    case "client_task":
      return `/tasks/${encodeURIComponent(n.entityId)}`;
    case "progress_report":
      return `/reports/${encodeURIComponent(n.entityId)}`;
    case "payment":
      return `/payments/${encodeURIComponent(n.entityId)}`;
    case "project":
      return `/?project=${encodeURIComponent(n.entityId)}`;
    case "project_stage":
      return n.data.projectId
        ? `/?project=${encodeURIComponent(n.data.projectId)}&stage=${encodeURIComponent(n.data.stageId ?? n.entityId)}`
        : "/";
    case "delivery_milestone":
      return (
        safePath(n.linkPath) ??
        (n.data.projectId
          ? `/?project=${encodeURIComponent(n.data.projectId)}`
          : "/")
      );
    case "attachment":
      return safePath(n.linkPath) ?? "/files";
    case "approval": {
      const taskId = n.data.approvalTaskId ?? n.data.targetId;
      return taskId ? `/tasks/${encodeURIComponent(taskId)}` : "/tasks";
    }
    case "advanced_approval":
      return `/approvals/${encodeURIComponent(n.entityId)}`;
    case "scope_change":
      return (
        safePath(n.linkPath) ??
        (n.data.projectId
          ? `/commercial?project=${encodeURIComponent(n.data.projectId)}`
          : "/commercial")
      );
    case "meeting_reservation":
    case "meeting_slot":
      return safePath(n.linkPath) ?? "/meetings";
    case "project_closure":
      return safePath(n.linkPath) ?? (n.data.projectId ? `/?project=${encodeURIComponent(n.data.projectId)}` : "/");
    default:
      return assertNever(entityType);
  }
}
